import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { crmFetch } from './crmApi';
import { PIPELINE_STAGES } from './constants';

const EMPTY = {
  jobId: '', firstName: '', lastName: '', email: '', phone: '', linkedinUrl: '',
  currentTitle: '', currentCompany: '', stage: 'sourced', notes: '',
};

export const CandidateModal = ({ open, onOpenChange, onSaved, candidate, defaultJobId, defaultStage }) => {
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!candidate;
  const jobLocked = isEdit || !!defaultJobId;

  useEffect(() => {
    if (!open) return;
    setForm(candidate ? {
      jobId: candidate.job_id || '', firstName: candidate.first_name || '', lastName: candidate.last_name || '',
      email: candidate.email || '', phone: candidate.phone || '', linkedinUrl: candidate.linkedin_url || '',
      currentTitle: candidate.current_title || '', currentCompany: candidate.current_company || '',
      stage: candidate.stage || 'sourced', notes: candidate.notes || '',
    } : { ...EMPTY, jobId: defaultJobId || '', stage: defaultStage || 'sourced' });
    if (!jobLocked) crmFetch('/jobs').then((r) => setJobs((r.data || []).filter((j) => j.status !== 'lost')));
  }, [open, candidate, defaultJobId, defaultStage, jobLocked]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!jobLocked && !form.jobId) { toast.error('Please select a job'); return; }
    if (!form.firstName.trim()) { toast.error('First name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        jobId: form.jobId, firstName: form.firstName, lastName: form.lastName, email: form.email,
        phone: form.phone, linkedinUrl: form.linkedinUrl, currentTitle: form.currentTitle,
        currentCompany: form.currentCompany, stage: form.stage, notes: form.notes,
      };
      const res = isEdit
        ? await crmFetch(`/candidates/${candidate.id}`, { method: 'PATCH', body: JSON.stringify(payload) })
        : await crmFetch('/candidates', { method: 'POST', body: JSON.stringify(payload) });
      if (!res.success) { toast.error(res.message || 'Failed to save candidate'); return; }
      toast.success(isEdit ? 'Candidate updated' : 'Candidate added');
      onOpenChange(false);
      onSaved && onSaved(res.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="candidate-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Candidate' : 'Add Candidate'}</DialogTitle>
          <DialogDescription>Someone in the pipeline for a recruitment job.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!jobLocked && (
            <div>
              <Label className="mb-1.5 block">Job *</Label>
              <Select value={form.jobId} onValueChange={(v) => set('jobId', v)}>
                <SelectTrigger data-testid="candidate-job-select"><SelectValue placeholder="Select job" /></SelectTrigger>
                <SelectContent>
                  {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}{j.accountName ? ` · ${j.accountName}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">First Name *</Label>
              <Input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} data-testid="candidate-firstname-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Last Name</Label>
              <Input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} data-testid="candidate-lastname-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Current Title</Label>
              <Input value={form.currentTitle} onChange={(e) => set('currentTitle', e.target.value)} data-testid="candidate-title-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Current Company</Label>
              <Input value={form.currentCompany} onChange={(e) => set('currentCompany', e.target.value)} data-testid="candidate-company-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="candidate-email-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Phone</Label>
              <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="candidate-phone-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">LinkedIn</Label>
              <Input value={form.linkedinUrl} onChange={(e) => set('linkedinUrl', e.target.value)} placeholder="https://…" data-testid="candidate-linkedin-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Stage</Label>
              <Select value={form.stage} onValueChange={(v) => set('stage', v)}>
                <SelectTrigger data-testid="candidate-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PIPELINE_STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="candidate-notes-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="candidate-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="candidate-save-btn">
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Candidate')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
